import { useStatsigClient } from '@statsig/react-bindings'
import {
  BlocksRenderer,
  type BlocksContent,
} from '@strapi/blocks-react-renderer'
import { type ReactQuery } from 'cms-api'
import React from 'react'
import styled from 'styled-components'
import { Flex } from 'components/Layout'
import { LinkWithText } from 'components/Link'
import { Text } from 'components/Typography'
import { STATSIG_EVENTS } from 'providers/statsig'
import { useUserAuth } from 'providers/userAuth'
import {
  COLOR_INTENT,
  FONT_SIZE,
  FONT_STACK,
  FONT_WEIGHT,
  SPACE,
  Z_INDEX,
} from 'Theme'

const StyledLinkWithText = styled(LinkWithText)`
  text-decoration: underline;
  white-space: nowrap;
`

interface TradeProgramRibbonProps extends React.PWC<unknown> {
  featureBarWidget: ReactQuery.WidgetsFeatureBarWidgetComponent
}

export const TradeProgramRibbon = React.forwardRef<
  HTMLDivElement,
  TradeProgramRibbonProps
>(({ featureBarWidget }, ref) => {
  const { client } = useStatsigClient()
  const { isExpertSignedIn, user } = useUserAuth()
  let variant = null

  if (isExpertSignedIn) {
    const experiment = client.getExperiment('phone_number_for_trade')
    variant = experiment.get('variant', 'control')
  }

  return (
    <Flex
      ref={ref}
      px={{ MOBILE: SPACE.PX_6, TABLET: SPACE.PX_9 }}
      backgroundColor={COLOR_INTENT.TRADE_PROGRAM_RIBBON.BACKGROUND}
      alignItems="center"
      justifyContent="center"
      zIndex={Z_INDEX.TRADE_PROGRAM_RIBBON}
      py={SPACE.PX_12}
    >
      <>
        {variant && variant === 'test' ? (
          <Text
            fontSize={FONT_SIZE.PX_14}
            color={COLOR_INTENT.TRADE_PROGRAM_RIBBON.TEXT}
            fontFamily={FONT_STACK.SANS}
            textAlign="center"
          >
            As a Trade Member, call or text{' '}
            <StyledLinkWithText
              color={COLOR_INTENT.TRADE_PROGRAM_RIBBON.TEXT}
              href="tel:310-853-2539"
              fontSize={FONT_SIZE.PX_14}
              fontFamily={FONT_STACK.SANS}
              fontWeight={FONT_WEIGHT.BOLD}
              onClick={() => {
                if (client) {
                  client.logEvent(STATSIG_EVENTS.TRADE_PHONE_CALL, user?.email)
                }
              }}
            >
              (310) 853-2539
            </StyledLinkWithText>{' '}
            to connect with one of our dedicated trade specialists.
          </Text>
        ) : (
          <>
            {featureBarWidget.text && (
              <BlocksRenderer
                content={featureBarWidget.text as BlocksContent}
                blocks={{
                  paragraph: ({ children }) => (
                    <Text
                      fontSize={FONT_SIZE.PX_14}
                      color={COLOR_INTENT.TRADE_PROGRAM_RIBBON.TEXT}
                      fontFamily={FONT_STACK.SANS}
                      textAlign="center"
                    >
                      {children}
                    </Text>
                  ),
                  link: ({ children, url }) => (
                    <StyledLinkWithText
                      color={COLOR_INTENT.TRADE_PROGRAM_RIBBON.TEXT}
                      href={url}
                      fontSize={FONT_SIZE.PX_14}
                      fontFamily={FONT_STACK.SANS}
                    >
                      {children}
                    </StyledLinkWithText>
                  ),
                }}
              />
            )}
          </>
        )}
      </>
    </Flex>
  )
})

TradeProgramRibbon.displayName = 'TradeProgramRibbon'
